// Client/src/RiskHeatmap.jsx
import React from "react";
import { getReportLabels } from "./reportI18n";

/**
 * RiskHeatmap
 *
 * Viser et klassisk 5x5 heatmap med Impact (Low → High) og Likelihood (Low → High)
 * baseret på deep_assessment.risk_register.
 *
 * Forventer et array af objekter:
 * [
 *   { risk: string, mitigation: string, impact: number(1-5), likelihood: number(1-5) },
 *   ...
 * ]
 *
 * Hvis der ikke er nogen valide risici, returnerer komponenten null.
 */

function normalizeLevel(value) {
  if (value == null) return null;

  if (typeof value === "number") {
    // klip til [1,5]
    const n = Math.round(value);
    if (n < 1 || n > 5) return null;
    return n;
  }

  const s = String(value).toLowerCase();
  if (s.includes("very high") || s.includes("critical")) return 5;
  if (s.includes("high")) return 4;
  if (s.includes("medium") || s.includes("mid")) return 3;
  if (s.includes("low")) return 2;

  return null;
}

function severityColor(score) {
  // score ~ impact * likelihood (1–25)
  if (score >= 20) {
    // meget høj risiko – mørk orange
    return "#ff8a33";
  }
  if (score >= 14) {
    // høj risiko – medium orange
    return "#ffb066";
  }
  if (score >= 8) {
    // medium risiko – lys orange
    return "#ffe0b8";
  }
  if (score >= 2) {
    // lav risiko – meget lys tone
    return "#fff4e6";
  }
  // ingen / næsten ingen
  return "#fafafa";
}

export default function RiskHeatmap({ result, reportLanguageCode = "en" }) {
  if (!result) return null;
  const labels = getReportLabels(reportLanguageCode);

  const deep =
    result.deep_assessment ||
    result.deepAssessment ||
    result.deep_assessment_v2 ||
    {};

  const rawRegister = Array.isArray(deep.risk_register)
    ? deep.risk_register
    : [];

  // Normaliser og filtrér risici der mangler impact/likelihood
  const risks = rawRegister
    .map((r) => {
      const impact = normalizeLevel(r.impact);
      const likelihood = normalizeLevel(r.likelihood);
      if (!impact || !likelihood) return null;

      return {
        label: r.risk || labels.notSpecified || "Unlabeled risk",
        mitigation: r.mitigation || "",
        impact,
        likelihood,
        score: impact * likelihood,
      };
    })
    .filter(Boolean)
    .map((r, idx) => ({ ...r, ref: `R${idx + 1}` }));

  // Hvis der stadig ingen risici er, viser vi ikke kortet
  if (!risks.length) return null;

  // Byg 5x5 grid: impact = rækker (5 high → 1 low), likelihood = kolonner (1–5)
  const grid = {};
  for (let impact = 1; impact <= 5; impact++) {
    for (let likelihood = 1; likelihood <= 5; likelihood++) {
      grid[`${impact}-${likelihood}`] = [];
    }
  }

  risks.forEach((r) => {
    const key = `${r.impact}-${r.likelihood}`;
    if (!grid[key]) grid[key] = [];
    grid[key].push(r);
  });

  const impactLabels = {
    5: labels.high,
    4: labels.medHigh,
    3: labels.medium,
    2: labels.medLow,
    1: labels.low,
  };

  const likelihoodLabels = {
    1: labels.rare,
    2: labels.unlikely,
    3: labels.possible,
    4: labels.likely,
    5: labels.almostCertain,
  };

  return (
    <div className="card risk-heatmap-card" style={{ marginTop: 16 }}>
      <style>{`
@media print {
  /*
   * Keep the heatmap figure (heading, explanation, scale, axes and complete
   * 5×5 grid) together. The risk legend may flow afterwards if needed.
   */
  .print-area .risk-heatmap-card .risk-heatmap-visual {
    display: flow-root !important;
    break-inside: avoid !important;
    page-break-inside: avoid !important;
  }

  .print-area .risk-heatmap-card .risk-heatmap-grid {
    break-inside: avoid !important;
    page-break-inside: avoid !important;
  }
}
      `}</style>

      <div className="risk-heatmap-visual">
        <h3 style={{ marginTop: 0 }}>6. {labels.riskHeatmap}</h3>
      <p className="muted small" style={{ marginTop: 4, maxWidth: 640 }}>
        {labels.heatmapIntro}
      </p>

      {/* Legend */}
      <div
        className="small"
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          margin: "8px 0 4px",
          flexWrap: "wrap",
        }}
      >
        <span className="muted">{labels.colourScale}:</span>
        <span
          style={{
            display: "inline-block",
            width: 16,
            height: 16,
            borderRadius: 4,
            background: severityColor(3),
            border: "1px solid #e0e0e0",
          }}
        />{" "}
        <span className="muted small">{labels.low}</span>
        <span
          style={{
            display: "inline-block",
            width: 16,
            height: 16,
            borderRadius: 4,
            background: severityColor(9),
            border: "1px solid #e0e0e0",
          }}
        />{" "}
        <span className="muted small">{labels.medium}</span>
        <span
          style={{
            display: "inline-block",
            width: 16,
            height: 16,
            borderRadius: 4,
            background: severityColor(16),
            border: "1px solid #e0e0e0",
          }}
        />{" "}
        <span className="muted small">{labels.high}</span>
        <span
          style={{
            display: "inline-block",
            width: 16,
            height: 16,
            borderRadius: 4,
            background: severityColor(22),
            border: "1px solid #e0e0e0",
          }}
        />{" "}
        <span className="muted small">{labels.veryHigh}</span>
      </div>

      {/* Axes labels */}
      <div
        className="small"
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginTop: 4,
          marginBottom: 4,
        }}
      >
        <span className="muted">
          {labels.impactAxis}{" "}
          <span aria-hidden="true" style={{ marginLeft: 4 }}>
            ↑
          </span>
        </span>
        <span className="muted">
          {labels.likelihoodAxis}{" "}
          <span aria-hidden="true" style={{ marginLeft: 4 }}>
            →
          </span>
        </span>
      </div>

      {/* Selve heatmap-griddet */}
      <div
        className="risk-heatmap-grid"
        style={{
          display: "grid",
          gridTemplateColumns: "80px repeat(5, minmax(0, 1fr))",
          gap: 4,
          alignItems: "stretch",
          marginTop: 4,
        }}
      >
        {/* Tom hjørne øverst til venstre */}
        <div />

        {/* Kolonne-headere: Likelihood 1–5 */}
        {Array.from({ length: 5 }, (_, i) => {
          const lvl = i + 1;
          return (
            <div
              key={`lh-header-${lvl}`}
              className="small muted"
              style={{
                textAlign: "center",
                padding: "4px 2px",
                fontWeight: 500,
              }}
            >
              {lvl} — {likelihoodLabels[lvl]}
            </div>
          );
        })}

        {/* Rækker: Impact 5 → 1 */}
        {Array.from({ length: 5 }, (_, rowIndex) => {
          const impact = 5 - rowIndex;
          return (
            <React.Fragment key={`row-${impact}`}>
              {/* Række-label (impact) */}
              <div
                className="small muted"
                style={{
                  paddingRight: 6,
                  textAlign: "right",
                  fontWeight: 500,
                }}
              >
                {impact} — {impactLabels[impact]}
              </div>

              {/* Celler i rækken */}
              {Array.from({ length: 5 }, (_, colIndex) => {
                const likelihood = colIndex + 1;
                const cellKey = `${impact}-${likelihood}`;
                const cellRisks = grid[cellKey] || [];
                const maxShow = 2;

                // Beregn samlet "værste" score i cellen til farve
                const worstScore = cellRisks.reduce(
                  (acc, r) => (r.score > acc ? r.score : acc),
                  0
                );
                const bg = severityColor(worstScore);

                return (
                  <div
                    key={cellKey}
                    style={{
                      borderRadius: 6,
                      padding: 6,
                      minHeight: 46,
                      border: "1px solid #e2e2e2",
                      background: bg,
                      boxSizing: "border-box",
                      overflow: "hidden",
                    }}
                  >
                    {cellRisks.length === 0 ? (
                      <span
                        className="muted small"
                        style={{ opacity: 0.4, fontSize: "0.7rem" }}
                      >
                        –
                      </span>
                    ) : (
                      <ul
                        style={{
                          margin: 0,
                          paddingLeft: 14,
                          listStyleType: "disc",
                          fontSize: "0.7rem",
                        }}
                      >
                        {cellRisks.slice(0, maxShow).map((r, idx) => (
                          <li key={idx} title={r.label}>{r.ref}</li>
                        ))}
                        {cellRisks.length > maxShow && (
                          <li>
                            + {cellRisks.length - maxShow}
                          </li>
                        )}
                      </ul>
                    )}
                  </div>
                );
              })}
            </React.Fragment>
          );
        })}
      </div>

      {/* Kort tekst om hvordan det skal bruges */}
        <p className="muted small" style={{ marginTop: 8 }}>
          {labels.heatmapFocus}
        </p>
      </div>

      <div className="risk-heatmap-legend small" style={{ marginTop: 10 }}>
        {risks.map((r) => (
          <div key={r.ref} className="risk-heatmap-legend-item">
            <strong>{r.ref}</strong> — {r.label}
            <span className="muted"> ({labels.impactAxis}: {r.impact}, {labels.likelihoodAxis}: {r.likelihood})</span>
          </div>
        ))}
      </div>
    </div>
  );
}
